import { useEffect, useId, useState } from "react";
import type { SourcePack } from "@desk/core";
import { ReconstructionLabel } from "./ReconstructionLabel.js";
import {
  enqueueArtifactCapture,
  resolveArtifactCaptureInput,
} from "../lib/artifact-capture.js";
import { artifactFingerprint } from "../lib/artifact-cache.js";
import { buildSourceRecordPosterUrl } from "../lib/source-record-poster.js";

export type ArtifactPreviewStatus = "idle" | "loading" | "ready" | "error" | "fallback";

export interface ArtifactPreviewProps {
  packId: string;
  pack: SourcePack;
  graphJson: string;
  label?: string;
  scoreText?: string;
  className?: string;
  /** Compact card for grids / portfolio. */
  compact?: boolean;
  width?: number;
  height?: number;
  /** When false, skip capture (lazy portfolio). */
  active?: boolean;
  testId?: string;
  /** Store cosmetic — never affects score. */
  resultCardTheme?: "default" | "portfolio-alt";
  onStatusChange?: (status: ArtifactPreviewStatus) => void;
}

/**
 * Async watermarked vertical preview via the hybrid-video export path.
 * Caches by full capture fingerprint (pack/version/graph/dims/score/theme); does not persist blobs.
 */
export function ArtifactPreview({
  packId,
  pack,
  graphJson,
  label,
  scoreText,
  className,
  compact = false,
  width,
  height,
  active = true,
  testId = "artifact-preview",
  resultCardTheme = "default",
  onStatusChange,
}: ArtifactPreviewProps) {
  const reactId = useId();
  const [status, setStatus] = useState<ArtifactPreviewStatus>("idle");
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLabel, setShowLabel] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const captureInput = resolveArtifactCaptureInput({
    packId,
    packVersion: pack.version,
    graphJson,
    width,
    height,
    scoreText,
    resultCardTheme,
    compact,
  });
  const fp = artifactFingerprint(captureInput);

  const setStatusNotify = (next: ArtifactPreviewStatus) => {
    setStatus(next);
    onStatusChange?.(next);
  };

  useEffect(() => {
    if (!graphJson) {
      setStatusNotify("idle");
      setUrl(null);
      setFallbackUrl(null);
      return;
    }
    // One-at-a-time capture: skip work when inactive, but keep the last settled frame.
    if (!active) {
      return;
    }
    let cancelled = false;
    setStatusNotify("loading");
    setError(null);
    void enqueueArtifactCapture({
      packId,
      pack,
      graphJson,
      scoreText: captureInput.scoreText,
      width: captureInput.width,
      height: captureInput.height,
      resultCardTheme: captureInput.resultCardTheme,
    }).then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setUrl(result.objectUrl);
        setShowLabel(result.showReconstructionLabel);
        setFallbackUrl(null);
        setStatusNotify("ready");
      } else {
        setError(result.reason);
        const poster = buildSourceRecordPosterUrl({
          pack,
          scoreText: captureInput.scoreText,
          width: captureInput.width,
          height: captureInput.height,
        });
        setFallbackUrl(poster);
        setUrl(null);
        setStatusNotify("fallback");
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fingerprint covers full capture identity
  }, [active, packId, pack, graphJson, fp]);

  const settled = status === "ready" || status === "error" || status === "fallback";

  return (
    <figure
      className={`artifact-preview${compact ? " compact" : ""}${className ? ` ${className}` : ""}${
        resultCardTheme === "portfolio-alt" ? " theme-alt" : ""
      }`}
      data-testid={testId}
      data-status={status}
      data-settled={settled ? "1" : "0"}
      data-fingerprint={fp}
      aria-busy={status === "loading"}
    >
      {label ? <figcaption className="artifact-caption">{label}</figcaption> : null}
      <div className="artifact-frame" data-testid={`${testId}-frame`}>
        {status === "ready" && url ? (
          <>
            <img src={url} alt={label ?? "Reel preview"} draggable={false} />
            <ReconstructionLabel show={showLabel} compact />
            <span className="watermark-chip" data-testid={`${testId}-watermark`}>
              {pack.exportSafety.requiredWatermark}
            </span>
          </>
        ) : status === "fallback" && fallbackUrl ? (
          <div className="artifact-fallback" data-testid={`${testId}-fallback`}>
            <img src={fallbackUrl} alt="Source record poster" draggable={false} />
            <p className="muted">Preview unavailable: source-record poster</p>
            {error ? <p className="muted">{error}</p> : null}
            <span className="watermark-chip" data-testid={`${testId}-watermark`}>
              {pack.exportSafety.requiredWatermark}
            </span>
          </div>
        ) : status === "error" ? (
          <div className="artifact-fallback" data-testid={`${testId}-error`}>
            <p>Preview unavailable</p>
            <p className="muted">{error}</p>
          </div>
        ) : (
          <div className="artifact-fallback" data-testid={`${testId}-loading`}>
            <p>{status === "idle" ? "–" : "Rendering…"}</p>
          </div>
        )}
      </div>
      <span className="sr-only">{reactId}</span>
    </figure>
  );
}
